import _ from 'lodash';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Component } from 'react';
import styled from 'styled-components';

import {
  Col,
  InputNumber,
  List,
  message,
  Row,
  Select,
  Skeleton,
  Typography,
} from 'antd';

import WorkOrderDetailsContext from 'containers/WorkOrder/WorkOrderDetailsContext';
import { convertToMonetary } from 'shared/utils';

const { Text } = Typography;
const { Option } = Select;

const Wrapper = styled.div`
  border: solid 1px #ddd;
  padding: 20px;
`;

class WorkOrderCostSummary extends Component {
  static contextType = WorkOrderDetailsContext;

  static propTypes = {
    childRef: PropTypes.func,
    lineItems: PropTypes.array,
    tax: PropTypes.object,
    misc: PropTypes.object,
    onChangeTaxType: PropTypes.func,
    onChangeTaxValue: PropTypes.func,
    onChangeMiscType: PropTypes.func,
    onChangeMiscValue: PropTypes.func,
    currentWorkOrder: PropTypes.object,
    rawData: PropTypes.any,
  };

  defaultTaxObj = {
    type: 'percentage',
    value: 0,
  };

  defaultMiscObj = {
    type: 'percentage',
    value: 0,
  };

  state = {
    data: [],
    isPartsCostInitialized: false,
    isLaborCostInitialized: false,
    userUpdatedTaxValues: false,
    laborAmount: 0,
    partsAmount: 0,
    totalAmount: 0,
  };

  tax = this.defaultTaxObj;
  misc = this.defaultMiscObj;
  lineItems = [];
  currentWorkOrder = {};
  rawData = null;

  constructor(props) {
    super(props);
    makeObservable(this, {
      currentLineItems: computed,
      total: computed,
      summaryItems: computed,
      localTax: computed,
      localMisc: computed,
      tax: observable,
      misc: observable,
      lineItems: observable,
      currentWorkOrder: observable,
      rawData: observable,
      updateState: action,
    });
    this.tax = props.tax || this.defaultTaxObj;
    this.misc = props.misc || this.defaultMiscObj;
    this.lineItems = props.lineItems || [];
    this.currentWorkOrder = props.currentWorkOrder || {};
    this.rawData = props.rawData;
  }

  componentDidMount() {
    if (this.props.childRef) {
      this.props.childRef(this);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tax !== this.props.tax) {
      this.tax = this.props.tax || this.defaultTaxObj;
    }
    if (prevProps.misc !== this.props.misc) {
      this.misc = this.props.misc || this.defaultMiscObj;
    }
    if (prevProps.lineItems !== this.props.lineItems) {
      this.lineItems = this.props.lineItems || [];
    }
    if (prevProps.currentWorkOrder !== this.props.currentWorkOrder) {
      this.currentWorkOrder = this.props.currentWorkOrder || {};
    }
    if (prevProps.rawData !== this.props.rawData) {
      this.rawData = this.props.rawData;
    }

    // Calculate new amounts
    const isPartsCostCalculated =
      this.rawData == null || this.rawData['Parts Cost'] === 'NOT FOUND';
    const isLaborCostCalculated =
      this.rawData == null || this.rawData['Labour Cost'] === 'NOT FOUND';

    let partsAmount;
    let laborAmount;

    if (this.currentWorkOrder && this.currentWorkOrder.partsTotal) {
      partsAmount = this.currentWorkOrder.partsTotal;
    } else {
      partsAmount = isPartsCostCalculated
        ? _.sumBy(this.lineItems, 'partsTotal')
        : this.state.partsAmount;
    }

    if (this.currentWorkOrder && this.currentWorkOrder.laborsTotal) {
      laborAmount = this.currentWorkOrder.laborsTotal;
    } else {
      laborAmount = isLaborCostCalculated
        ? _.sumBy(this.lineItems, 'laborTotal')
        : this.state.laborAmount;
    }

    // Update state if values have changed
    if (
      partsAmount !== this.state.partsAmount ||
      laborAmount !== this.state.laborAmount
    ) {
      this.updateState({
        partsAmount,
        laborAmount,
      });
    }

    // Update total if needed
    const newTotal = this.total;
    if (newTotal !== this.state.totalAmount) {
      this.updateState({ totalAmount: newTotal });
    }
  }

  updateState = (newState) => {
    this.setState(newState);
  };

  get localTax() {
    return this.tax;
  }

  get localMisc() {
    return this.misc;
  }

  get currentLineItems() {
    return this.lineItems;
  }

  get summaryItems() {
    const isPartsCostCalculated =
      this.rawData == null || this.rawData['Parts Cost'] === 'NOT FOUND';
    const isLaborCostCalculated =
      this.rawData == null || this.rawData['Labour Cost'] === 'NOT FOUND';

    let partsAmount;
    let laborAmount;

    if (this.currentWorkOrder && this.currentWorkOrder.partsTotal) {
      partsAmount = this.currentWorkOrder.partsTotal;
    } else {
      partsAmount = isPartsCostCalculated
        ? _.sumBy(this.lineItems, 'partsTotal')
        : this.state.partsAmount;
    }

    if (this.currentWorkOrder && this.currentWorkOrder.laborsTotal) {
      laborAmount = this.currentWorkOrder.laborsTotal;
    } else {
      laborAmount = isLaborCostCalculated
        ? _.sumBy(this.lineItems, 'laborTotal')
        : this.state.laborAmount;
    }

    return [
      {
        description: 'Labors',
        amount: laborAmount,
      },
      {
        description: 'Parts',
        amount: partsAmount,
      },
      {
        description: 'Tax',
        amount: this.localTax.value,
        amountType: this.localTax.type || 'absolute',
        extraFields: [
          <Select
            defaultValue={this.localTax.type || 'absolute'}
            onChange={this.onChangeTaxType}
            key="type"
          >
            <Option key="percentage" value="percentage">
              %
            </Option>
            <Option key="absolute" value="absolute">
              $
            </Option>
          </Select>,
          <InputNumber
            key="value"
            defaultValue={this.localTax.value || 0}
            onChange={this.onChangeTaxValue}
            formatter={(value) =>
              `${this.localTax.type === 'absolute' ? '$ ' : ''}${value}`.replace(
                /\B(?=(\d{3})+(?!\d))/g,
                ','
              )
            }
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            style={{ width: '100px', marginRight: '30px', marginLeft: '5px' }}
          />,
        ],
      },
    ];
  }

  get total() {
    if (this.currentWorkOrder && this.currentWorkOrder.costsTotal) {
      return this.currentWorkOrder.costsTotal;
    }

    let costsTotal;
    if (
      this.rawData === null ||
      this.rawData['Total Cost'] === 'NOT FOUND' ||
      this.state.userUpdatedTaxValues
    ) {
      costsTotal = _.sumBy(this.currentLineItems, (i) =>
        _.sum([i.laborTotal, i.partsTotal])
      );
      const taxValue =
        this.localTax.type === 'percentage'
          ? ((this.localTax.value || 0) / 100) * costsTotal
          : parseFloat(this.localTax.value || 0);
      costsTotal += taxValue;
      const miscValue =
        this.localMisc.type === 'percentage'
          ? ((this.localMisc.value || 0) / 100) * costsTotal
          : parseFloat(this.localMisc.value || 0);
      costsTotal += miscValue;
    } else {
      costsTotal = _.get(this, 'rawData[\'Total Cost\']', 0);
    }

    return costsTotal;
  }

  onChangeTaxType = (value) => {
    if (this.props.onChangeTaxType) {
      this.props.onChangeTaxType(value);
    }
  };

  onChangeTaxValue = (value) => {
    if (this.props.onChangeTaxValue) {
      this.props.onChangeTaxValue(value);
    }
  };

  onChangeMiscType = (value) => {
    if (this.props.onChangeMiscType) {
      this.props.onChangeMiscType(value);
    }
  };

  onChangeMiscValue = (value) => {
    if (this.props.onChangeMiscValue) {
      this.props.onChangeMiscValue(value);
    }
  };

  render() {
    return (
      <Wrapper>
        <List
          dataSource={this.summaryItems}
          renderItem={(item) => (
            <List.Item>
              <Row style={{ width: '100%' }}>
                <Col span={12}>
                  <Text>{item.description}</Text>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                  {item.extraFields ? (
                    <div
                      style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                      {item.extraFields}
                    </div>
                  ) : (
                    <Text>
                      {convertToMonetary(item.amount, item.amountType)}
                    </Text>
                  )}
                </Col>
              </Row>
            </List.Item>
          )}
        />
        <Row style={{ marginTop: '20px' }}>
          <Col span={12}>
            <Text strong>Total</Text>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Text strong>{convertToMonetary(this.total)}</Text>
          </Col>
        </Row>
      </Wrapper>
    );
  }
}

export default observer(WorkOrderCostSummary);
